import type { RealtimeEvent, WsClientMessage, WsRole, WsServerMessage } from "@luckymenu/core";
// The zod-free entry: the storefront loads this client in the diner's browser.
import { WS_CLOSE } from "@luckymenu/core/client";

/**
 * The WebSocket client of every LuckyMenu front end: staff web, staff
 * Android, the storefront and admin. It works with the browser's and React
 * Native's global `WebSocket`. See docs/architecture/realtime-notifications.md.
 *
 * The socket is a hint channel. The client only says "something changed";
 * screens refetch through the API. So it keeps no event log: after a
 * reconnect it tells its listeners to refetch everything live (`onReconnect`),
 * and while it is offline for more than 10 seconds `status.offline` is true,
 * which shows the banner and starts polling (see `bindQueryClient`).
 */

/** The part of the WebSocket API the client uses. Browsers and React Native have it. */
export interface WebSocketLike {
	readonly readyState: number;
	send(data: string): void;
	close(code?: number, reason?: string): void;
	onopen: ((event: unknown) => void) | null;
	onmessage: ((event: { data: unknown }) => void) | null;
	onclose: ((event: { code: number; reason?: string }) => void) | null;
	onerror: ((event: unknown) => void) | null;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

/** What a staff socket authenticates with. Diners need nothing: their cookie goes with the upgrade. */
export type StaffAuth = {
	/** A fresh Clerk session token. */
	token: string;
	restaurantId: string;
	/** An admin support-session token (staff web only). */
	supportToken?: string | null;
};

/** What an admin socket authenticates with (the admin app, Phase 8): joins `a:platform`. */
export type AdminAuth = {
	/** A fresh Clerk session token of a platform admin. */
	token: string;
	admin: true;
};

export type RealtimeState =
	/** Not started, or waiting for a sign-in (`getAuth` gave null). */
	| "idle"
	/** Opening, authenticating, or waiting to reconnect. */
	| "connecting"
	/** Authorised and receiving events. */
	| "open"
	/** Refused for good (forbidden, not found) or stopped. `restart()` tries again. */
	| "stopped";

export type RealtimeStatus = {
	state: RealtimeState;
	/** Not open for more than `offlineAfterMs`. Show the offline banner and poll. */
	offline: boolean;
	role: WsRole | null;
	/** The last close code from the server, such as 4003 or 1012. */
	lastCloseCode: number | null;
};

export type RealtimeClientOptions = {
	/** `wss://<host>/api/ws`. See `socketUrl`. */
	url: string;
	/**
	 * Staff only: a fresh token and the active restaurant, asked for on every
	 * connect and before each token expires. Null means signed out or no
	 * restaurant yet: the client waits until `restart()`.
	 */
	getAuth?: () => Promise<StaffAuth | AdminAuth | null>;
	/** Defaults to the global `WebSocket`. */
	createSocket?: WebSocketFactory;
	/** Reconnect delays: doubling from `initialMs` up to `maxMs`, with jitter. */
	backoff?: { initialMs?: number; maxMs?: number };
	/** Defaults to 10 seconds (realtime-notifications.md). */
	offlineAfterMs?: number;
	/** Reconnect when nothing, not even a ping, arrived for this long. Defaults to 60 seconds. */
	silenceMs?: number;
	/** Send a fresh `auth` this long before the token expires. Defaults to 15 seconds. */
	refreshBeforeMs?: number;
	/** For tests. Defaults to `Math.random`. */
	random?: () => number;
};

/** The socket URL for a page or an API origin: `https://x` → `wss://x/api/ws`. */
export function socketUrl(origin: string): string {
	return `${origin.replace(/\/$/, "").replace(/^http/, "ws")}/api/ws`;
}

const OPEN = 1;
/** Close codes after which reconnecting cannot help until something changes. */
const PERMANENT = new Set<number>([WS_CLOSE.forbidden, WS_CLOSE.notFound]);

export class RealtimeClient {
	private readonly options: RealtimeClientOptions;
	private ws: WebSocketLike | null = null;
	private status: RealtimeStatus = {
		state: "idle",
		offline: false,
		role: null,
		lastCloseCode: null,
	};
	private running = false;
	private attempt = 0;
	private everReady = false;
	private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
	private offlineTimer: ReturnType<typeof setTimeout> | undefined;
	private silenceTimer: ReturnType<typeof setTimeout> | undefined;
	private refreshTimer: ReturnType<typeof setTimeout> | undefined;
	private readonly statusListeners = new Set<() => void>();
	private readonly eventListeners = new Set<(event: RealtimeEvent) => void>();
	private readonly reconnectListeners = new Set<() => void>();

	constructor(options: RealtimeClientOptions) {
		this.options = options;
	}

	/** Connects, and keeps reconnecting until `stop()`. */
	start(): void {
		if (this.running) return;
		this.running = true;
		this.connect();
	}

	/** Closes the socket and stops reconnecting. */
	stop(): void {
		this.running = false;
		this.clearTimers();
		this.dropSocket();
		this.setStatus({ state: "stopped", offline: false, role: null });
	}

	/**
	 * Starts again from scratch: after sign-in, a restaurant switch or a
	 * permanent refusal. Screens are refetched when it is ready again.
	 */
	restart(): void {
		this.stop();
		this.attempt = 0;
		this.start();
	}

	/**
	 * Reconnects now instead of waiting out the backoff: call it when the
	 * device comes back online or the app returns to the foreground.
	 */
	reconnectNow(): void {
		if (!this.running || this.status.state === "open") return;
		if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
		this.reconnectTimer = undefined;
		this.attempt = 0;
		this.dropSocket();
		this.connect();
	}

	getStatus(): RealtimeStatus {
		return this.status;
	}

	/** Calls `listener` whenever the status changes. For `useSyncExternalStore`. */
	subscribe(listener: () => void): () => void {
		this.statusListeners.add(listener);
		return () => this.statusListeners.delete(listener);
	}

	/** Every event that reaches this socket. */
	onEvent(listener: (event: RealtimeEvent) => void): () => void {
		this.eventListeners.add(listener);
		return () => this.eventListeners.delete(listener);
	}

	/** After each reconnect, not the first connect: events may have been missed, so refetch. */
	onReconnect(listener: () => void): () => void {
		this.reconnectListeners.add(listener);
		return () => this.reconnectListeners.delete(listener);
	}

	private setStatus(patch: Partial<RealtimeStatus>): void {
		const next = { ...this.status, ...patch };
		if (
			next.state === this.status.state &&
			next.offline === this.status.offline &&
			next.role === this.status.role &&
			next.lastCloseCode === this.status.lastCloseCode
		) {
			return;
		}
		this.status = next;
		for (const listener of this.statusListeners) listener();
	}

	private clearTimers(): void {
		for (const timer of [
			this.reconnectTimer,
			this.offlineTimer,
			this.silenceTimer,
			this.refreshTimer,
		]) {
			if (timer) clearTimeout(timer);
		}
		this.reconnectTimer = undefined;
		this.offlineTimer = undefined;
		this.silenceTimer = undefined;
		this.refreshTimer = undefined;
	}

	private dropSocket(): void {
		const ws = this.ws;
		this.ws = null;
		if (!ws) return;
		ws.onopen = null;
		ws.onmessage = null;
		ws.onclose = null;
		ws.onerror = null;
		try {
			ws.close(1000, "client");
		} catch {
			// Already closed.
		}
	}

	private armOffline(): void {
		if (this.offlineTimer || this.status.offline) return;
		this.offlineTimer = setTimeout(() => {
			this.offlineTimer = undefined;
			if (this.status.state !== "open") this.setStatus({ offline: true });
		}, this.options.offlineAfterMs ?? 10_000);
	}

	private armSilence(): void {
		if (this.silenceTimer) clearTimeout(this.silenceTimer);
		this.silenceTimer = setTimeout(() => {
			// The connection died without a close: start over.
			this.dropSocket();
			this.scheduleReconnect(null);
		}, this.options.silenceMs ?? 60_000);
	}

	private send(message: WsClientMessage): void {
		if (this.ws?.readyState === OPEN) this.ws.send(JSON.stringify(message));
	}

	private async sendAuth(): Promise<boolean> {
		const getAuth = this.options.getAuth;
		if (!getAuth) return true;
		let auth: StaffAuth | AdminAuth | null;
		try {
			auth = await getAuth();
		} catch {
			// No token right now (offline, Clerk slow): reconnect later.
			this.dropSocket();
			this.scheduleReconnect(null);
			return false;
		}
		if (!this.running) return false;
		if (!auth) {
			this.clearTimers();
			this.dropSocket();
			this.setStatus({ state: "idle", role: null });
			return false;
		}
		if ("admin" in auth) {
			this.send({ type: "adminAuth", token: auth.token });
			return true;
		}
		this.send({
			type: "auth",
			token: auth.token,
			restaurantId: auth.restaurantId,
			...(auth.supportToken ? { supportToken: auth.supportToken } : {}),
		});
		return true;
	}

	private connect(): void {
		if (!this.running) return;
		this.setStatus({ state: "connecting" });
		this.armOffline();
		const create =
			this.options.createSocket ??
			((url: string) =>
				new (globalThis as unknown as { WebSocket: new (u: string) => WebSocketLike }).WebSocket(
					url,
				));
		let ws: WebSocketLike;
		try {
			ws = create(this.options.url);
		} catch {
			this.scheduleReconnect(null);
			return;
		}
		this.ws = ws;
		ws.onopen = () => {
			this.armSilence();
			void this.sendAuth();
		};
		ws.onmessage = (event) => this.onMessage(event.data);
		ws.onerror = () => {
			// A close event follows.
		};
		ws.onclose = (event) => {
			if (this.ws !== ws) return;
			this.ws = null;
			this.scheduleReconnect(event.code);
		};
	}

	private onMessage(data: unknown): void {
		this.armSilence();
		let message: WsServerMessage;
		try {
			message = JSON.parse(String(data)) as WsServerMessage;
		} catch {
			return;
		}
		switch (message.type) {
			case "ping":
				this.send({ type: "pong" });
				return;
			case "ready": {
				this.attempt = 0;
				if (this.offlineTimer) clearTimeout(this.offlineTimer);
				this.offlineTimer = undefined;
				this.setStatus({ state: "open", offline: false, role: message.role });
				this.scheduleRefresh(message.expiresAt);
				if (this.everReady) for (const listener of this.reconnectListeners) listener();
				this.everReady = true;
				return;
			}
			case "event":
				for (const listener of this.eventListeners) listener(message.event);
				return;
			case "error":
				return;
		}
	}

	private scheduleRefresh(expiresAt: string | null): void {
		if (this.refreshTimer) clearTimeout(this.refreshTimer);
		this.refreshTimer = undefined;
		if (!expiresAt || !this.options.getAuth) return;
		const lead = this.options.refreshBeforeMs ?? 15_000;
		const delay = Math.max(1000, Date.parse(expiresAt) - Date.now() - lead);
		this.refreshTimer = setTimeout(() => {
			this.refreshTimer = undefined;
			void this.sendAuth();
		}, delay);
	}

	private scheduleReconnect(code: number | null): void {
		if (this.silenceTimer) clearTimeout(this.silenceTimer);
		if (this.refreshTimer) clearTimeout(this.refreshTimer);
		this.silenceTimer = undefined;
		this.refreshTimer = undefined;
		if (code !== null) this.setStatus({ lastCloseCode: code });
		if (!this.running) return;
		if (code !== null && PERMANENT.has(code)) {
			this.running = false;
			if (this.offlineTimer) clearTimeout(this.offlineTimer);
			this.offlineTimer = undefined;
			this.setStatus({ state: "stopped", role: null });
			return;
		}
		this.setStatus({ state: "connecting", role: null });
		this.armOffline();
		const initial = this.options.backoff?.initialMs ?? 1000;
		const max = this.options.backoff?.maxMs ?? 30_000;
		let delay = Math.min(max, initial * 2 ** this.attempt);
		if (code === WS_CLOSE.restart) delay = Math.min(delay, 500);
		if (code === WS_CLOSE.tooManySockets) delay = max;
		this.attempt++;
		const jittered = Math.round(delay * (0.5 + (this.options.random ?? Math.random)() / 2));
		if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = undefined;
			this.connect();
		}, jittered);
	}
}
